import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/* @section: resources */
export default function Resources() {
  const { t } = useLanguage();

  const articles = [
    {
      category: { en: "Business Structure", ko: "사업 구조" },
      titleEn: "LLC vs. S-Corp: Which One Makes Sense for Your Business?",
      titleKo: "LLC vs. S-Corp: 어떤 구조가 내 사업에 맞을까?",
      summaryEn: "Understanding the key differences between LLC and S-Corp can save you thousands in self-employment tax. We break down the pros, cons, and when each structure makes sense for your income level.",
      summaryKo: "LLC와 S-Corp의 주요 차이점을 이해하면 자영업세에서 수천 달러를 절약할 수 있습니다. 각 구조의 장단점과 소득 수준에 따라 어떤 구조가 적합한지 설명합니다.",
      readTime: "5 min",
      tag: "business",
    },
    {
      category: { en: "Tax Deductions", ko: "세금 공제" },
      titleEn: "What Business Expenses Are Deductible?",
      titleKo: "어떤 사업 비용이 공제 가능한가?",
      summaryEn: "From home office to vehicle use, meals, and software subscriptions — learn what the IRS allows as legitimate business deductions and how to document them properly.",
      summaryKo: "홈오피스부터 차량 사용, 식비, 소프트웨어 구독까지 — IRS가 정당한 사업 공제로 허용하는 항목과 적절한 문서화 방법을 알아봅니다.",
      readTime: "6 min",
      tag: "deductions",
    },
    {
      category: { en: "Estimated Tax", ko: "예상 세금" },
      titleEn: "Why Estimated Tax Payments Matter for Self-Employed Owners",
      titleKo: "자영업자에게 예상세금 납부가 중요한 이유",
      summaryEn: "Missing estimated tax payments leads to penalties and a surprise tax bill in April. Learn how to calculate quarterly payments and avoid common mistakes.",
      summaryKo: "예상세금 납부를 놓치면 가산세와 4월의 세금 폭탄으로 이어집니다. 분기별 납부액 계산 방법과 흔한 실수를 피하는 방법을 알아봅니다.",
      readTime: "4 min",
      tag: "estimated",
    },
    {
      category: { en: "Small Business", ko: "스몰비즈니스" },
      titleEn: "Common Tax Mistakes Small Business Owners Make",
      titleKo: "스몰비즈니스 오너들이 자주 하는 세금 실수",
      summaryEn: "Mixing personal and business finances, missing payroll deadlines, and not tracking mileage are just a few costly mistakes. Here's what to watch for and how to avoid them.",
      summaryKo: "개인 및 사업 재정 혼용, 급여 기한 누락, 주행거리 미추적은 비용이 많이 드는 몇 가지 실수에 불과합니다. 주의해야 할 사항과 피하는 방법을 소개합니다.",
      readTime: "7 min",
      tag: "mistakes",
    },
    {
      category: { en: "절세 전략", ko: "절세 전략" },
      titleEn: "Tax Savings Differences: Sole Proprietor vs. S-Corp",
      titleKo: "개인사업자와 S-Corp 절세 차이",
      summaryEn: "Depending on your net income, switching from a sole proprietor to an S-Corp can save $5,000–$15,000 per year in self-employment taxes. Here's a realistic breakdown.",
      summaryKo: "순소득에 따라 개인사업자에서 S-Corp으로 전환하면 연간 $5,000~$15,000의 자영업세를 절약할 수 있습니다. 현실적인 분석을 소개합니다.",
      readTime: "5 min",
      tag: "strategy",
    },
    {
      category: { en: "IRS 대응", ko: "IRS 대응" },
      titleEn: "Received an IRS Letter? Here's What to Do First",
      titleKo: "IRS Letter를 받았을 때 먼저 해야 할 일",
      summaryEn: "Most IRS letters are not audits, but they still require timely action. Here's a step-by-step guide on what to read, what not to do, and when to get professional help.",
      summaryKo: "대부분의 IRS 편지는 감사가 아니지만 여전히 신속한 조치가 필요합니다. 무엇을 읽어야 하고, 하지 말아야 할 것과 언제 전문가의 도움을 받아야 하는지 단계별 안내입니다.",
      readTime: "4 min",
      tag: "irs",
    },
    {
      category: { en: "S-Corp Planning", ko: "S-Corp 플랜" },
      titleEn: "What Is a Reasonable Salary for S-Corp Owners in San Jose?",
      titleKo: "샌호세 S-Corp 오너의 합리적 급여란?",
      summaryEn: "The IRS requires S-Corp shareholder-employees to pay themselves a reasonable salary. Learn how to determine the right amount based on your industry and income to stay compliant and save on taxes.",
      summaryKo: "IRS는 S-Corp 주주 직원이 합리적인 급여를 지급받도록 요구합니다. 컴플라이언스를 유지하고 세금을 절약하기 위해 업종과 소득에 따른 적정 급여 결정 방법을 알아봅니다.",
      readTime: "6 min",
      tag: "strategy",
    },
    {
      category: { en: "Bookkeeping", ko: "장부 관리" },
      titleEn: "Why Good Bookkeeping Is the Foundation of Tax Savings",
      titleKo: "좋은 장부 관리가 절세의 기반인 이유",
      summaryEn: "Accurate bookkeeping is not just about compliance — it's the data your tax advisor needs to find deductions and plan effectively. Here's how to set up a simple, effective system.",
      summaryKo: "정확한 장부 관리는 컴플라이언스뿐만 아니라 공제를 발견하고 효과적으로 계획하기 위한 데이터입니다. 간단하고 효과적인 시스템을 구축하는 방법을 알아봅니다.",
      readTime: "5 min",
      tag: "business",
    },
    {
      category: { en: "Year-End Planning", ko: "연말 절세" },
      titleEn: "Year-End Tax Checklist for Small Business Owners",
      titleKo: "스몰비즈니스 오너를 위한 연말 세금 체크리스트",
      summaryEn: "Before December 31st, there are critical actions that can reduce your tax bill. From funding retirement accounts to timing income and expenses, this checklist covers what matters most.",
      summaryKo: "12월 31일 이전에 세금을 줄일 수 있는 중요한 조치들이 있습니다. 은퇴 계좌 납입부터 소득 및 비용 타이밍까지, 이 체크리스트가 가장 중요한 것들을 다룹니다.",
      readTime: "8 min",
      tag: "estimated",
    },
    {
      category: { en: "Korean Business", ko: "한인 비즈니스" },
      titleEn: "Tax Considerations for Korean-American Small Business Owners in the Bay Area",
      titleKo: "베이 지역 한인 스몰비즈니스 오너를 위한 세금 고려 사항",
      summaryEn: "Korean-American entrepreneurs in San Jose, Santa Clara, Cupertino, and the Bay Area face unique tax challenges. This guide covers common situations including foreign income, banking considerations, and bilingual tax filing.",
      summaryKo: "샌호세, 산타클라라, 쿠퍼티노 등 베이 지역 한인 사업주들은 독특한 세금 문제에 직면합니다. 해외 소득, 은행 거래, 이중언어 세금보고 등 흔한 상황을 다룹니다.",
      readTime: "7 min",
      tag: "business",
    },
  ];

  const tagColors: Record<string, string> = {
    business: "bg-blue-50 text-blue-700 border-blue-100",
    deductions: "bg-green-50 text-green-700 border-green-100",
    estimated: "bg-amber-50 text-amber-700 border-amber-100",
    mistakes: "bg-red-50 text-red-700 border-red-100",
    strategy: "bg-purple-50 text-purple-700 border-purple-100",
    irs: "bg-orange-50 text-orange-700 border-orange-100",
  };

  return (
    <>
      <SEO
        title="Tax Tips & Resources | Small Business Tax Planning San Jose | NexBiz Tax Advisor"
        description="Free bilingual tax resources for small business owners in San Jose and the Bay Area — LLC vs S-Corp, deductions, estimated tax, IRS notices, and more."
      />
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("Resources & Tax Tips", "세금 팁 & 자료")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t("Tax Tips for Small Business Owners", "스몰비즈니스 오너를 위한 세금 팁")}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "Practical, bilingual tax education to help you make better decisions throughout the year.",
                "연중 더 나은 결정을 내릴 수 있도록 돕는 실용적인 이중언어 세금 교육."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer banner */}
      <section className="bg-muted border-b border-border py-3 px-4">
        <div className="container mx-auto max-w-5xl">
          <p className="text-xs text-muted-foreground text-center">
            {t(
              "Articles below are for general educational purposes only and do not constitute tax advice. Contact us for personalized guidance.",
              "아래 글은 일반 교육 목적으로만 제공되며 세금 조언을 구성하지 않습니다. 개인 맞춤 안내는 문의해주세요."
            )}
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {articles.map((article, i) => (
              <motion.div key={i} variants={fadeUp}>
                <div className="h-full bg-card border border-border rounded-xl overflow-hidden hover:border-accent/40 hover:shadow-md transition-all duration-200 flex flex-col">
                  <div className="p-6 flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${tagColors[article.tag] || "bg-muted text-muted-foreground"}`}>
                        {t(article.category.en, article.category.ko)}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </div>
                    </div>
                    <h3 className="font-bold text-foreground text-base leading-snug mb-3">
                      {t(article.titleEn, article.titleKo)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(article.summaryEn, article.summaryKo)}
                    </p>
                  </div>
                  <div className="px-6 pb-5">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                    >
                      {t("Ask us about this topic", "이 주제에 대해 문의하기")}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-14 p-8 bg-primary rounded-2xl text-center"
          >
            <BookOpen className="w-10 h-10 text-accent mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-primary-foreground mb-3">
              {t("Have a Specific Tax Question?", "특정 세금 질문이 있으신가요?")}
            </h3>
            <p className="text-primary-foreground/70 mb-6 max-w-xl mx-auto">
              {t(
                "Every situation is different. Contact us for guidance tailored to your business and personal tax situation.",
                "모든 상황은 다릅니다. 귀하의 비즈니스와 개인 세금 상황에 맞는 안내를 위해 문의하세요."
              )}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-accent text-white font-semibold hover:bg-accent/90 transition-colors shadow-md"
            >
              {t("Send Your Tax Question", "세금 질문 보내기")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
